import { Component, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { TemplateService, Question, Prompt } from '../services/template.service';

interface Task {
  name: string;
  description: string;
  icon: string;
  color: string;
  complexity?: string;
  templateId: string;
  questionId: string;
  instructions: string;
  isEdit: boolean;
  prompts: Prompt[];
}

@Component({
  selector: 'app-landing',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.css'
})
export class LandingComponent implements OnInit, AfterViewInit {
  currentTheme: 'light' | 'dark' = 'light';
  templateId: string | null = null;
  templateName = '';
  isLoading = false;

  tasks: Task[] = [];

  // Search and sort state
  searchQuery = '';
  sortColumn: 'name' | 'questionId' | 'status' = 'status';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Computed stats
  get totalQuestions(): number {
    return this.tasks.length;
  }

  get questionsWithPrompt(): number {
    return this.tasks.filter(t => t.isEdit).length;
  }

  get questionsWithoutPrompt(): number {
    return this.tasks.filter(t => !t.isEdit).length;
  }

  // Filtered and sorted tasks
  get filteredTasks(): Task[] {
    let result = this.tasks;

    // Filter by search query (case-insensitive)
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase().trim();
      result = result.filter(task =>
        task.name.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.questionId.toString().includes(query)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      let comparison = 0;
      switch (this.sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'questionId':
          comparison = a.questionId.localeCompare(b.questionId, undefined, { numeric: true });
          break;
        case 'status':
          comparison = (a.isEdit === b.isEdit) ? 0 : a.isEdit ? 1 : -1;
          break;
      }
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }

  toggleSort(column: 'name' | 'questionId' | 'status') {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private templateService: TemplateService
  ) {}

  ngOnInit() {
    this.initializeTheme();
    this.loadFromRoute();
  }

  loadFromRoute() {
    // Check query params for templateid
    this.route.queryParams.subscribe(params => {
      const tid = params['templateid'] || params['templateId'];
      if (tid && tid !== 'null') {
        this.templateId = tid;
        // Store in service for persistence across pages
        this.templateService.setTemplateId(this.templateId);
        this.loadTemplateQuestions();
      } else {
        // No templateId in URL - check if we have a cached templateId
        const cachedTemplateId = this.templateService.getCachedTemplateId();
        if (cachedTemplateId) {
          this.templateId = cachedTemplateId;
          this.loadTemplateQuestions();
        } else {
          // No template loaded - show empty state
          this.templateId = null;
          this.isLoading = false;
        }
      }
    });
  }

  loadTemplateQuestions() {
    if (!this.templateId) return;

    // Check for cached data first
    const cachedData = this.templateService.getCachedData(this.templateId);
    
    if (cachedData) {
      // Use cached data - no API call needed
      this.processTemplateData(cachedData);
      return;
    }

    this.isLoading = true;
    this.templateService.getQuestions(this.templateId).subscribe({
      next: (data) => {
        // Cache the response in the service
        this.templateService.setCachedData(data);
        this.processTemplateData(data);
      },
      error: (err) => {
        console.error('Failed to load questions:', err);
        this.isLoading = false;
        this.tasks = [];
        setTimeout(() => this.initializeLucideIcons(), 50);
      }
    });
  }

  private processTemplateData(data: any) {
    this.templateName = data.templateName;
    this.tasks = data.questions.map((q: any, index: number) => ({
      name: q.name,
      description: `Question ID: ${q.id}`,
      icon: this.getIconForDataType(q.dataType),
      color: this.getColorForIndex(index),
      templateId: this.templateId!,
      questionId: q.id,
      instructions: q.prompts && q.prompts.length > 0 ? q.prompts.map((p: any) => p.text).join('\n\n') : '',
      isEdit: q.prompts && q.prompts.length > 0,
      prompts: q.prompts || []
    }));
    
    this.isLoading = false;
    
    // Re-initialize icons after DOM updates
    setTimeout(() => this.initializeLucideIcons(), 50);
    setTimeout(() => this.initializeLucideIcons(), 200);
  }

  private getIconForDataType(dataType: string): string {
    const icons: { [key: string]: string } = {
      'Photo': 'camera',
      'Text': 'type',
      'Number': 'hash',
      'Date': 'calendar',
      'Checkbox': 'check-square'
    };
    return icons[dataType] || 'file';
  }

  private getColorForIndex(index: number): string {
    const colors = ['cyan', 'violet', 'rose', 'emerald', 'amber', 'blue'];
    return colors[index % colors.length];
  }

  ngAfterViewInit() {
    this.initializeLucideIcons();
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark';
    if (savedTheme) {
      this.currentTheme = savedTheme;
      this.applyTheme(savedTheme);
    }
  }

  setTheme(theme: 'light' | 'dark') {
    this.currentTheme = theme;
    this.applyTheme(theme);
    localStorage.setItem('theme', theme);
  }

  private applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  selectTask(task: Task) {
    // Build return URL from current location
    const returnUrl = window.location.pathname + window.location.search;
    
    if (this.templateId && task.questionId) {
      // Navigate with templateId and questionId in the URL, pass returnUrl in state
      this.router.navigate(['/details', this.templateId, task.questionId], {
        state: { returnUrl }
      });
    } else {
      // Fallback to query params
      this.router.navigate(['/details'], {
        queryParams: {
          name: task.name,
          templateId: task.templateId,
          instructions: task.instructions
        },
        state: { returnUrl }
      });
    }
  }

  getTaskShortId(templateId: string): string {
    return templateId.split('-')[1] || '8291';
  }

  initializeLucideIcons() {
    if (typeof (window as any).lucide !== 'undefined') {
      (window as any).lucide.createIcons({
        attrs: {
          'stroke-width': 1.5
        }
      });
    } else {
      // Retry after a short delay if lucide is not loaded yet
      setTimeout(() => this.initializeLucideIcons(), 100);
    }
  }
}
